import React, { useState, useEffect, useRef, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { getCalendarEvents, refreshFromCalendar } from '../../services/api';
import type { CalendarEvent, ScheduledPublication } from '../../services/api';
import PublicationDetailModal from './PublicationDetailModal';
import { GlassButton } from '../ui/GlassButton';
import { RefreshCw, CalendarDays } from 'lucide-react';
import './SchedulingPage.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


interface Props {
    projectId: string;
}

const SchedulingCalendar: React.FC<Props> = ({ projectId }) => {
    const [events, setEvents] = useState<CalendarEvent[]>([]);
    const [loading, setLoading] = useState(true);
    const [initialLoad, setInitialLoad] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedPublicationId, setSelectedPublicationId] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const calendarRef = useRef<FullCalendar>(null);

    const loadEvents = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            // Get current calendar view dates if calendar is initialized
            let startDate: string | undefined;
            let endDate: string | undefined;

            if (calendarRef.current) {
                const api = calendarRef.current.getApi();
                const view = api.view;
                startDate = view.currentStart.toISOString();
                endDate = view.currentEnd.toISOString();
            }

            const response = await getCalendarEvents(projectId, startDate, endDate);
            setEvents(response.events);
        } catch (err: any) {
            console.error('Failed to load calendar events:', err);
            setError(getApiError(err, 'Failed to load calendar events'));
        } finally {
            setLoading(false);
            setInitialLoad(false);
        }
    }, [projectId]);

    useEffect(() => {
        loadEvents();
    }, [loadEvents]);

    const handleSyncFromCalendar = async () => {
        setSyncing(true);
        setSyncMessage(null);

        try {
            const result = await refreshFromCalendar(projectId);

            if (result.success || result.rebuilt > 0) {
                const parts: string[] = [`Rebuilt ${result.rebuilt} calendar entries.`];
                if (result.updated > 0) parts.push(`${result.updated} rescheduled`);
                if (result.added > 0) parts.push(`${result.added} added`);
                if (result.cancelled > 0) parts.push(`${result.cancelled} cancelled`);
                if (result.unchanged > 0) parts.push(`${result.unchanged} unchanged`);
                setSyncMessage({ type: 'success', text: parts.join(' · ') });
                await loadEvents();
            } else {
                const errText = result.errors.length > 0
                    ? result.errors.join('; ')
                    : 'No content calendar found for this project.';
                setSyncMessage({ type: 'error', text: errText });
            }
        } catch (err: any) {
            setSyncMessage({
                type: 'error',
                text: getApiError(err, 'Sync failed. Check the server logs.')
            });
        } finally {
            setSyncing(false);
        }
    };

    const handleEventClick = (info: any) => {
        const publicationId = info.event.id;
        setSelectedPublicationId(publicationId);
        setModalOpen(true);
    };

    const handleDateSet = useCallback(() => {
        // Only reload after initial load to prevent infinite loop
        if (!initialLoad) {
            loadEvents();
        }
    }, [initialLoad, loadEvents]);

    const handleModalClose = () => {
        setModalOpen(false);
        setSelectedPublicationId(null);
        // Reload events to reflect any changes
        loadEvents();
    };

    if (initialLoad && loading) {
        return <div className="calendar-loading">Loading calendar...</div>;
    }

    if (error) {
        return (
            <div className="calendar-error">
                <strong>Error:</strong> {error}
                <GlassButton 
                    variant="secondary" 
                    onClick={loadEvents} 
                    style={{ marginLeft: '1rem' }}
                >
                    Retry
                </GlassButton>
            </div>
        );
    }

    return (
        <div className="scheduling-calendar">
            <div className="calendar-header">
                <div className="calendar-header-top">
                    <h2>Publication Calendar</h2>
                    <GlassButton
                        variant="secondary"
                        onClick={handleSyncFromCalendar}
                        disabled={syncing}
                        loading={syncing}
                        icon={<RefreshCw size={15} />}
                    >
                        {syncing ? 'Syncing…' : 'Sync from Content Calendar'}
                    </GlassButton>
                </div>

                {syncMessage && (
                    <div className={`calendar-sync-message calendar-sync-message--${syncMessage.type}`}>
                        {syncMessage.text}
                    </div>
                )}

                {events.length === 0 && !loading && (
                    <div className="calendar-info-box">
                        <p>
                            <CalendarDays size={15} style={{ flexShrink: 0 }} />
                            <strong>No scheduled publications yet.</strong> Connect a publishing destination (WordPress credentials in the Credentials tab, or Webflow in Settings), then schedule content for automatic publication.
                        </p>
                    </div>
                )}
                <div className="calendar-legend">
                    <span className="legend-item">
                        <span className="legend-color platform-wordpress"></span>
                        WordPress
                    </span>
                    <span className="legend-item">
                        <span className="legend-color platform-linkedin"></span>
                        LinkedIn
                    </span>
                    <span className="legend-item">
                        <span className="legend-color platform-twitter"></span>
                        Twitter
                    </span>
                    <span className="legend-item">
                        <span className="legend-color platform-facebook"></span>
                        Facebook
                    </span>
                    <span className="legend-item">
                        <span className="legend-color platform-instagram"></span>
                        Instagram
                    </span>
                    <span className="legend-item">
                        <span className="legend-color platform-edm"></span>
                        EDM
                    </span>
                </div>
            </div>

            <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'dayGridMonth,timeGridWeek,timeGridDay'
                }}
                events={events}
                eventClick={handleEventClick}
                datesSet={handleDateSet}
                height="auto"
                eventTimeFormat={{
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                }}
                slotLabelFormat={{
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                }}
            />

            {modalOpen && selectedPublicationId && (
                <PublicationDetailModal
                    publicationId={selectedPublicationId}
                    onClose={handleModalClose}
                />
            )}
        </div>
    );
};

export default SchedulingCalendar;
